<template>
  <section>
    <form @submit.prevent="submitFormAddress">
      <!-- Person type -->
      <div class="row mb-3 no-gutters">
        <div class="col-sm-3 col-12">
          <label>{{ $t('message.company_address.type') }}</label>
        </div>
        <div class="col-sm-3 col-6">
          <div class="exp">
            <div class="checkbox">
              <input type="radio"
                      id="juristicPerson"
                      name="isCompany"
                      :value="1"
                      v-model="company.is_company"
                      v-validate="'required'"/>
              <label for="juristicPerson" class="mb-0">
                <span><!-- This span is needed to create the "checkbox" element --></span>
                {{ $t('message.company_address.juristic_person') }}
              </label>
            </div>
          </div>
        </div>
        <div class="col-sm-3 col-6">
          <div class="exp">
            <div class="checkbox">
              <input type="radio"
                      id="normalPerson"
                      name="isCompany"
                      :value="0"
                      v-model="company.is_company"/>
              <label for="normalPerson" class="mb-0">
                <span><!-- This span is needed to create the "checkbox" element --></span>
                {{ $t('message.company_address.normal_person') }}
              </label>
            </div>
          </div>
        </div>
        <div class="col-sm-3 col-12">
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('companyType')">
            {{ $t('message.required_field') }}
          </div>
        </div>
      </div>

      <!-- Company name -->
      <div class="row mb-3 no-gutters">
        <div class="col-sm-3">
          <label for="company">
            {{ companyNameLabel }}
          </label>
        </div>
        <div class="col-sm-9">
          <input
            id="company"
            type="text"
            :class="{'form-control': true, 'is-invalid': errors.has('company')}"
            v-validate="'required'"
            v-model.trim="company.company"
            name="company"
          />
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('company')">
            {{ $t('message.required_field') }}
          </div>
        </div>
      </div>

      <!-- Tax Id -->
      <div class="row mb-3 no-gutters">
        <div class="col-sm-3">
          <label for="tax_id">{{ $t('message.company_address.tax_id') }}</label>
        </div>
        <div class="col-sm-9">
          <input
            id="taxId"
            type="text"
            :class="{'form-control': true, 'is-invalid': errors.has('taxId')}"
            v-validate="'required|digits:13'"
            v-model.trim="company.tax_id"
            name="taxId"
            maxlength="13"
          />
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('taxId')">
            {{ $t('message.required_field') }}
          </div>
        </div>
      </div>

      <!-- Company Type -->
      <div class="row mb-3 no-gutters" v-if="company.is_company === null || company.is_company === 1">
        <div class="col-sm-3 col-12">
          <label>{{ $t('message.company_address.type') }}</label>
        </div>
        <div class="col-sm-3 col-6">
          <div class="exp">
            <div class="checkbox">
              <input type="radio"
                      id="hq"
                      name="companyType"
                      :value="1"
                      v-model="company.company_type"
                      v-validate="'required'"/>
              <label for="hq" class="mb-0">
                <span><!-- This span is needed to create the "checkbox" element --></span>
                {{ $t('message.company_address.hq') }}
              </label>
            </div>
          </div>
        </div>
        <div class="col-sm-3 col-6">
          <div class="exp">
            <div class="checkbox">
              <input type="radio"
                      id="branch"
                      name="companyType"
                      :value="2"
                      v-model="company.company_type"/>
              <label for="branch" class="mb-0">
                <span><!-- This span is needed to create the "checkbox" element --></span>
                {{ $t('message.company_address.branch') }}
              </label>
            </div>
          </div>
        </div>
        <div class="col-sm-3 col-12">
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('companyType')">
            {{ $t('message.required_field') }}
          </div>
        </div>
      </div>

      <!-- Tin Number -->
      <div class="row mb-3 no-gutters" v-if="company.is_company === 1 && company.company_type === 2">
        <div class="col-sm-3">
          <label class="tinNumber">{{ $t('message.company_address.tin_number') }}</label>
        </div>
        <div class="col-sm-9">
          <input
            id="tinNumber"
            name="tinNumber"
            type="text"
            :class="{'form-control': true, 'is-invalid': errors.has('tinNumber')}"
            v-validate="'required'"
            v-model.trim="company.tin_number"
          />
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('tinNumber')">
            {{ $t('message.required_field') }}
          </div>
        </div>
      </div>

      <!-- Email (user section tag to prevent email rule effects to tel input below)-->
      <section class="row mb-3 no-gutters">
        <div class="col-sm-3">
          <label for="email">{{ $t('message.company_address.email') }}</label>
        </div>
        <div class="col-sm-9">
          <input
            id="email"
            name="email"
            type="text"
            :class="{'form-control': true, 'is-invalid': errors.has('email')}"
            v-validate="'required|email'"
            v-model="company.email"
          />
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('email')">
            {{ $t('message.required_field') }}
          </div>
        </div>
      </section>
      
      <!-- Phone-->
      <div class="row mb-3 no-gutters">
        <div class="col-sm-3">
          <label for="tel">{{ $t('message.company_address.tel') }}</label>
        </div>
        <div class="col-sm-9">
          <input
            id="tel"
            name="tel"
            type="tel"
            :class="{ 'form-control': true, 'is-invalid': errors.has('tel') }"
            v-model="company.tel"
            v-validate="{ required: true, min: 10, max: 15, regex: /^\+?[0-9-]+$/ }">
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('tel')">
            {{ $t('message.required_field') }}
          </div>
        </div>
      </div>

      <!-- Company Address Section-->
      <div class="row mt-5 mb-3 no-gutters">
        <div class="col">
          <h3>
            {{ (company.is_company === null || company.is_company === 1) ? $t('message.company_address.company_address') : $t('message.company_address.customer_address') }}
          </h3>
        </div>
      </div>
      <!-- Company Address-->
      <div class="row mb-3 no-gutters">
        <div class="col-sm-3">
          <label for="companyAddress">{{ $t('message.my_listings.address') }}</label>
        </div>
        <div class="col-sm-9">
          <textarea
            id="companyAddress"
            name="companyAddress"
            :placeholder="$t('message.company_address.location_placeholder')"
            :class="{'form-control': true, 'is-invalid': errors.has('companyAddress')}"
            v-validate="'required'"
            v-model.trim="company.company_address"
            rows="3"
          ></textarea>
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('companyAddress')">
            {{ $t('message.required_field') }}
          </div>
        </div>
      </div>
      <!-- Company Province-->
      <div class="row mb-3 no-gutters">
        <div class="col-sm-3">
          <label for="companyAddressId">{{ $t('message.company_address.province') }}</label>
        </div>
        <!-- Company Address-->
        <div class="col-sm-9">
          <select
            id="addressProvinceId"
            name="addressProvinceId"
            v-model="company.address_province_id"
            @change="changeAddressProvince(company.address_province_id)"
            class="form-control"
            :class="{ 'form-alert': errors.has('addressProvinceId') }"
            v-validate="'required'" 
          >
            <option :value="null">{{
              $t('message.booking.contact_step.set_province')
            }}</option>
            <option
              :value="addressProvince.id"
              v-for="(addressProvince, index) in sortedProvinces"
              :key="index"
            >
              {{ addressProvince | multiLangName }}
            </option>
          </select>
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('addressProvinceId')">
            {{ $t('message.required_field') }}
          </div>
        </div>
      </div>

      <!-- Company District-->
      <div class="row mb-3 no-gutters">
        <div class="col-sm-3">
          <label for="districtId">{{ $t('message.company_address.district') }}</label>
        </div>
        <!-- Company Address-->
        <div class="col-sm-9">
          <select
            id="districtId"
            name="districtId"
            v-model="company.district_id"
            @change="changeDistrict(company.district_id)"
            class="form-control"
            :class="{ 'form-alert': errors.has('districtId') }"
            v-validate="'required'"
          >
            <option :value="null">{{
              $t('message.booking.contact_step.set_district')
            }}</option>
            <option
              :value="district.id"
              v-for="(district, index) in sortedDistricts"
              :key="index"
            >
              {{ district | multiLangName }}
            </option>
          </select>
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('districtId')">
            {{ $t('message.required_field') }}
          </div>
        </div>
      </div>

      
      <!-- Company Sub District-->
      <div class="row mb-3 no-gutters">
        <div class="col-sm-3">
          <label for="subDistrictId">{{ $t('message.company_address.sub_district') }}</label>
        </div>
        <!-- Company Address-->
        <div class="col-sm-9">
          <select
            id="subDistrictId"
            name="subDistrictId"
            v-model="company.sub_district_id"
            class="form-control"
            :class="{ 'form-alert': errors.has('subDistrictId') }"
            v-validate="'required'"
          >
            <option :value="null">{{
              $t('message.booking.contact_step.set_sub_district')
            }}</option>
            <option
              :value="subDistrcict.id"
              v-for="(subDistrcict, index) in sortedSubDistricts"
              :key="index"
            >
              {{ subDistrcict.zip_code }} - {{ subDistrcict | multiLangName }}
            </option>
          </select>
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('subDistrictId')">
            {{ $t('message.required_field') }}
          </div>
        </div>
      </div>
    </form>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'CompanyAddressForm',
  data () {
    return {
      company: {
        is_company: null,
        company: null,
        tax_id: null,
        tin_number: null,
        company_address: null,
        email: null,
        tel: null,
        user_id: null,
        address_province_id: null,
        district_id: null,
        sub_district_id: null,
        company_type: null,
        is_same_address: null
      },
      selectedCompanyAddress: null,
      sortBy: (this.$i18n.locale === 'th') ? 'name_th' : 'name_en'
    }
  },
  watch: {
    'company.is_company': function (newValue, oldValue) {
      if (newValue === 0) {
        this.company.tin_number = null
      }
    },
    'company.company_type': function (newValue, oldValue) {
      if (newValue === 1) {
        this.company.tin_number = null
      }
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/getUser'
    }),
    ...mapState('companyAddress', ['companies', 'selectedIndex']),
    ...mapState('addressProvince', ['addressProvinces', 'districts', 'subDistricts']),
    companyNameLabel () {
      switch (this.company.is_company) {
        case null:
          return this.$i18n.t('message.company_address.company_customer_name')
        case 0:
          return this.$i18n.t('message.company_address.customer_name')
        case 1:
          return this.$i18n.t('message.company_address.company_name')
      }
    },
    sortedProvinces () {
      return this._.orderBy(this.addressProvinces, [this.sortBy], ['asc'])
    },
    sortedDistricts () {
      return this._.orderBy(this.districts, [this.sortBy], ['asc'])
    },
    sortedSubDistricts () {
      return this._.orderBy(this.subDistricts, [this.sortBy], ['asc'])
    }
  },
  methods: {
    setEmptyForm () {
      this.company.is_company = null
      this.company.company = null
      this.company.tax_id = null
      this.company.tin_number = null
      this.company.company_address = null
      this.company.email = null
      this.company.tel = null
      this.company.user_id = null
      this.company.address_province_id = null
      this.company.district_id = null
      this.company.sub_district_id = null
      this.company.company_type = null
    },
    async setUserAddressData (index) {
      this.selectedCompanyAddress = this.companies[this.selectedIndex]
      this.company.is_company = this.selectedCompanyAddress.is_company
      this.company.company = this.selectedCompanyAddress.company
      this.company.tax_id = this.selectedCompanyAddress.tax_id
      this.company.tin_number = this.selectedCompanyAddress.tin_number
      this.company.company_address = this.selectedCompanyAddress.company_address
      this.company.email = this.selectedCompanyAddress.email
      this.company.tel = this.selectedCompanyAddress.tel
      this.company.user_id = this.selectedCompanyAddress.user_id
      this.company.address_province_id = this.selectedCompanyAddress.address_province_id
      this.company.district_id = this.selectedCompanyAddress.district_id
      this.company.sub_district_id = this.selectedCompanyAddress.sub_district_id
      this.company.company_type = this.selectedCompanyAddress.company_type
      this.company.is_same_address = this.selectedCompanyAddress.is_same_address

      await this.fetchDistricts(this.company.address_province_id)
      await this.fetchSubDistricts(this.company.district_id)
    },
    async submitFormAddress (fromPage, formAction) {
      this.$validator.validate().then(async valid => {
        if (valid) {
          this.company.user_id = this.user.id
          const submitData = {
            company: this.company,
            fromPage: fromPage
          }
          if (fromPage === 'company-address') {
            if (formAction === 'add') {
              await this.$store.dispatch(
                'companyAddress/ADD_COMPANY',
                submitData
              )
              this.$emit('finished', true)
            } else if (formAction === 'edit') {
              this.company.id = this.selectedCompanyAddress.id
              await this.$store.dispatch(
                'companyAddress/UPDATE_COMPANY',
                submitData
              )
              this.$emit('finished', true)
            }
          } else {
            await this.$store.dispatch('companyAddress/ADD_COMPANY', submitData)
          }
        }
      })
    },
    changeAddressProvince (id) {
      this.fetchDistricts(id)
      this.company.district_id = null
      this.company.sub_district_id = null
    },
    changeDistrict (id) {
      this.fetchSubDistricts(id)
      this.company.sub_district_id = null
    },
    fetchAddressProvinces () {
      const queryData = {
        is_all: 1
      }
      this.$store.dispatch('addressProvince/LOAD_ADDRESS_PROVINCES', queryData)
    },
    fetchDistricts (provinceId) {
      if (provinceId) {
        const queryData = {
          province_id: provinceId
        }
        this.$store.dispatch('addressProvince/LOAD_DISTRICTS', queryData)
      }
    },
    fetchSubDistricts (districtId) {
      if (districtId) {
        const queryData = {
          district_id: districtId
        }
        this.$store.dispatch('addressProvince/LOAD_SUB_DISTRICTS', queryData)
      }
    }
  },
  mounted () {
    if (this.selectedIndex !== null && this.selectedIndex >= 0) {
      this.setUserAddressData(this.selectedIndex)
    } else {
      this.setEmptyForm()
    }
  },
  created () {
    this.fetchAddressProvinces()
  }
}
</script>



// WEBPACK FOOTER //
// src/components/booking/components/CompanyAddressForm.vue